import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Lock } from 'lucide-react';
import { useState } from 'react';
import { AssetDrawer } from '@/components/accountant/asset-drawer';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  assetStatus,
  lastWholeMonth,
  lifeLabel,
  methodLabel,
  monthLabel,
  useAssets,
  useDepreciationPreview,
  useDepreciationRuns,
  type DepreciationRun,
} from '@/lib/accountant';
import { api } from '@/lib/api';
import { ltr } from '@/lib/arabic';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { i18n, useT } from '@/lib/i18n';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { homeCurrency } from '@/lib/org-locale';

/** Isolates a number or code inside an Arabic sentence; English stays as typed. */
const iso = (v: string): string => (i18n.language === 'ar' ? ltr(v) : v);

export const Route = createFileRoute('/accounting/fixed-assets/')({ component: FixedAssets });

/** Screen A12 (document 06): fixed asset register and depreciation runs. */
function FixedAssets() {
  const orgId = useOrgId();
  const t = useT();
  const me = useMe();
  const navigate = useNavigate();
  const assets = useAssets();
  const isStaff = me.data?.isStaff ?? false;
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState(false);
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  const list = assets.data ?? [];
  const total = (k: 'cost' | 'accumulated' | 'netBookValue') =>
    formatMinor(
      list.filter((a) => a.status === 'active').reduce((s, a) => s + (toMinor(a[k]) ?? 0n), 0n),
    );
  return (
    <div>
      <PageHeader
        title={t('accountingPages.fixedAssetsList.title')}
        description={t('accountingPages.fixedAssetsList.description')}
        actions={
          isStaff ? (
            <>
              <Button variant="outline" onClick={() => setRunning(true)} disabled={!list.length}>
                {t('accountingPages.fixedAssetsList.runDepreciation')}
              </Button>
              <Button onClick={() => setCreating(true)}>
                {t('accountingPages.fixedAssetsList.newAsset')}
              </Button>
            </>
          ) : null
        }
      />
      {assets.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : assets.isError ? (
        <EmptyState
          title={t('accountingPages.fixedAssetsList.loadError')}
          hint={assets.error.message}
        />
      ) : !list.length ? (
        <EmptyState
          title={t('accountingPages.fixedAssetsList.emptyTitle')}
          hint={t('accountingPages.fixedAssetsList.emptyHint')}
          action={
            isStaff ? (
              <Button onClick={() => setCreating(true)}>
                {t('accountingPages.fixedAssetsList.newAsset')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('accountingPages.fixedAssetsList.colAssetNo')}</TableHead>
                <TableHead>{t('accountingPages.fixedAssetsList.colName')}</TableHead>
                <TableHead>{t('accountingPages.fixedAssetsList.colAssetAccount')}</TableHead>
                <TableHead>{t('accountingPages.fixedAssetsList.colInService')}</TableHead>
                <TableHead>{t('accountingPages.fixedAssetsList.colMethod')}</TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.fixedAssetsList.colCost')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.fixedAssetsList.colAccumulated')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.fixedAssetsList.colNbv')}
                </TableHead>
                <TableHead>{t('accountingPages.fixedAssetsList.colDepreciatedThrough')}</TableHead>
                <TableHead>{t('accountingPages.fixedAssetsList.colStatus')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((a) => (
                <TableRow
                  key={a.id}
                  className="cursor-pointer"
                  data-testid={`asset-${a.number}`}
                  onClick={() =>
                    void navigate({
                      to: '/accounting/fixed-assets/$assetId',
                      params: { assetId: a.id },
                    })
                  }
                >
                  <TableCell>
                    <Link
                      to="/accounting/fixed-assets/$assetId"
                      params={{ assetId: a.id }}
                      className="text-primary hover:underline"
                    >
                      {a.number}
                    </Link>
                  </TableCell>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>{a.assetAccount}</TableCell>
                  <TableCell>{formatDate(a.inServiceDate)}</TableCell>
                  <TableCell>
                    {t('accountingPages.fixedAssetsList.methodLife', {
                      method: methodLabel(a.method),
                      life: lifeLabel(a.usefulLifeMonths),
                    })}
                  </TableCell>
                  <TableCell className="tnum text-end">{formatAmount(a.cost)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(a.accumulated)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(a.netBookValue)}</TableCell>
                  <TableCell>
                    {a.depreciatedThrough ? monthLabel(a.depreciatedThrough) : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.status === 'disposed' ? 'outline' : 'secondary'}>
                      {assetStatus(a)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell colSpan={5}>
                  {t('accountingPages.fixedAssetsList.totalInService')}
                </TableCell>
                <TableCell className="tnum text-end">{total('cost')}</TableCell>
                <TableCell className="tnum text-end">{total('accumulated')}</TableCell>
                <TableCell className="tnum text-end">{total('netBookValue')}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
      <Runs />
      <AssetDrawer
        open={creating}
        asset={null}
        onOpenChange={setCreating}
        onSaved={(a) =>
          void navigate({ to: '/accounting/fixed-assets/$assetId', params: { assetId: a.id } })
        }
      />
      <RunDialog open={running} onClose={() => setRunning(false)} />
    </div>
  );
}

function Runs() {
  const orgId = useOrgId();
  const t = useT();
  const me = useMe();
  const qc = useQueryClient();
  const runs = useDepreciationRuns();
  const [undoing, setUndoing] = useState<DepreciationRun | null>(null);
  const undo = useMutation({
    mutationFn: ({ r, reason }: { r: DepreciationRun; reason: string }) =>
      api<DepreciationRun[]>(`/orgs/${orgId}/depreciation/runs/${r.id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (_v, { r }) => {
      await qc.invalidateQueries();
      setUndoing(null);
      toast(t('accountingPages.fixedAssetsList.runUndone', { month: monthLabel(r.month) }));
    },
  });
  if (!runs.data?.length) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-2 text-base font-semibold">
        {t('accountingPages.fixedAssetsList.runsTitle')}
      </h2>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('accountingPages.fixedAssetsList.colMonth')}</TableHead>
              <TableHead>{t('accountingPages.fixedAssetsList.colRun')}</TableHead>
              <TableHead className="text-end">
                {t('accountingPages.fixedAssetsList.colAssets')}
              </TableHead>
              <TableHead className="text-end">
                {t('accountingPages.fixedAssetsList.colAmount')}
              </TableHead>
              <TableHead>{t('accountingPages.fixedAssetsList.colJournalNo')}</TableHead>
              <TableHead>{t('accountingPages.fixedAssetsList.colStatus')}</TableHead>
              <TableHead>{t('accountingPages.fixedAssetsList.colWhen')}</TableHead>
              <TableHead className="w-20">
                {t('accountingPages.fixedAssetsList.colAction')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{monthLabel(r.month)}</TableCell>
                <TableCell>
                  {r.trigger === 'schedule'
                    ? t('accountingPages.fixedAssetsList.triggerScheduled')
                    : r.trigger === 'seed'
                      ? t('accountingPages.fixedAssetsList.triggerSetUp')
                      : t('accountingPages.fixedAssetsList.triggerManual')}
                </TableCell>
                <TableCell className="tnum text-end">{r.assetCount}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(r.total)}</TableCell>
                <TableCell>
                  {r.entryId ? (
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: r.entryId }}
                      className="text-primary hover:underline"
                    >
                      {r.entryNo}
                    </Link>
                  ) : null}
                </TableCell>
                <TableCell>
                  {r.status === 'failed' ? (
                    <span className="flex items-start gap-1 text-xs text-destructive">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden /> {r.error}
                    </span>
                  ) : (
                    <Badge variant={r.status === 'posted' ? 'secondary' : 'outline'}>
                      {r.status === 'posted'
                        ? t('accountingPages.fixedAssetsList.statusPosted')
                        : t('accountingPages.fixedAssetsList.statusUndone')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {formatDateTime(r.createdAt)}
                  {r.createdBy ? (
                    <span className="text-muted-foreground"> · {r.createdBy}</span>
                  ) : null}
                </TableCell>
                <TableCell>
                  {me.data?.isStaff && r.canUndo ? (
                    <Button variant="link" size="sm" className="px-1" onClick={() => setUndoing(r)}>
                      {t('accountingPages.fixedAssetsList.undo')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ReasonDialog
        open={undoing !== null}
        title={t('accountingPages.fixedAssetsList.undoTitle', {
          month: undoing ? monthLabel(undoing.month) : '',
        })}
        description={t('accountingPages.fixedAssetsList.undoDescription')}
        confirmLabel={t('accountingPages.fixedAssetsList.undoConfirm')}
        destructive
        pending={undo.isPending}
        error={undo.error?.message ?? null}
        onConfirm={(reason) => undoing && undo.mutate({ r: undoing, reason })}
        onClose={() => {
          undo.reset();
          setUndoing(null);
        }}
      />
    </section>
  );
}

/** The months a run can go through: the last whole month and the eleven before it. */
function monthOptions(today: string): string[] {
  const out: string[] = [];
  let m = lastWholeMonth(today);
  for (let i = 0; i < 24; i++) {
    out.push(m);
    m = lastWholeMonth(m);
  }
  const current = `${today.slice(0, 7)}-01`;
  return [current, ...out];
}

function RunDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const orgId = useOrgId();
  const t = useT();
  const qc = useQueryClient();
  const today = todayIso();
  const [through, setThrough] = useState(lastWholeMonth(today));
  const preview = useDepreciationPreview(open ? through : null);
  const run = useMutation({
    mutationFn: () =>
      api<{
        posted: Array<{ month: string; total: string }>;
        failed: { month: string; error: string } | null;
      }>(`/orgs/${orgId}/depreciation/run`, {
        method: 'POST',
        body: JSON.stringify({ throughMonth: through }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      if (r.failed) {
        toast(
          t('accountingPages.fixedAssetsList.postedPartial', {
            count: r.posted.length,
            month: monthLabel(r.failed.month),
            error: r.failed.error,
          }),
          { tone: 'error' },
        );
      } else {
        toast(t('accountingPages.fixedAssetsList.postedAll', { count: r.posted.length }));
      }
      onClose();
    },
  });
  const months = preview.data?.months ?? [];
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          run.reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{t('accountingPages.fixedAssetsList.runDepreciation')}</DialogTitle>
          <DialogDescription>
            {t('accountingPages.fixedAssetsList.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{run.error?.message ?? null}</ErrorBanner>
          <Field label={t('accountingPages.fixedAssetsList.throughEndOf')} className="w-56">
            {(id) => (
              <NativeSelect id={id} value={through} onChange={(e) => setThrough(e.target.value)}>
                {monthOptions(today).map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {preview.isPending ? (
            <p className="text-sm text-muted-foreground">
              {t('accountingPages.fixedAssetsList.workingOut')}
            </p>
          ) : !months.length ? (
            <p className="text-sm text-muted-foreground">
              {t('accountingPages.fixedAssetsList.nothingDue', { month: monthLabel(through) })}
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <tbody>
                  {months.map((m) => (
                    <tr key={m.month} className="border-b align-top last:border-0">
                      <td className="w-28 px-3 py-2 font-medium">
                        <span className="inline-flex items-center gap-1">
                          {monthLabel(m.month)}
                          {m.locked ? (
                            <span title={t('accountingPages.fixedAssetsList.closedMonthTitle')}>
                              <Lock
                                className="size-3.5 text-destructive"
                                aria-label={t('accountingPages.fixedAssetsList.closedMonth')}
                              />
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {m.lines.map((l) => `${l.number} ${l.name}`).join(', ')}
                      </td>
                      <td className="tnum px-3 py-2 text-end">{formatAmount(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.data && months.length ? (
            <p className="tnum text-end text-sm font-semibold">
              {t('accountingPages.fixedAssetsList.total', {
                currency: homeCurrency(),
                amount: iso(formatAmount(preview.data.total)),
              })}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!months.length || run.isPending} onClick={() => run.mutate()}>
            {run.isPending
              ? t('accountingPages.fixedAssetsList.posting')
              : t('accountingPages.fixedAssetsList.postDepreciation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
